/**
 * The sim's ray query (D-013): float64 world rays against the Rapier world, used by projectiles,
 * torpedoes and AI obstacle avoidance through the `RaycastFn` type.
 */
import RAPIER from '@dimforge/rapier3d-compat'
import { EID_ASTEROID, type Physics, type Ray, type RayHit, type RaycastFn } from './physics'

/** Rapier's raw (WASM binding) types, reached through the public API (the package does not export them). */
type RawVector = ReturnType<typeof RAPIER.VectorOps.intoRaw>
type RawCastFn = RAPIER.BroadPhase['raw']['castRayAndGetNormal']
type RawHit = NonNullable<ReturnType<RawCastFn>>

/**
 * The raw broad-phase ray cast (`RawBroadPhase.castRayAndGetNormal`), typed with the optional arguments the
 * binding accepts as `undefined` (Rapier's own `BroadPhase.castRayAndGetNormal` passes them so).
 */
interface RawRayCast {
  castRayAndGetNormal(
    narrowPhase: RAPIER.NarrowPhase['raw'],
    bodies: RAPIER.RigidBodySet['raw'],
    colliders: RAPIER.ColliderSet['raw'],
    rayOrig: RawVector,
    rayDir: RawVector,
    maxToi: number,
    solid: boolean,
    filterFlags: number,
    filterGroups: number | undefined,
    filterExcludeCollider: number | undefined,
    filterExcludeRigidBody: number | undefined,
    filterPredicate: Parameters<RawCastFn>[11] | undefined,
  ): RawHit | undefined
}

/**
 * Builds the `RaycastFn` for `physics`: the closest hit of a solid ray from a float64 world origin along a
 * unit direction, skipping the rigid body of `excludeEid` (the shooter; -1 = none).
 *
 * - D-011: the origin is rebased to the physics origin before the cast, and the hit point is rebuilt in
 *   float64 world space from the world origin (`o + d × toi`), so it keeps full precision anywhere.
 * - Colliders missing from `physics.colliderEid` report `EID_ASTEROID` (static, the safe default).
 * - D-014: nothing is allocated per cast (the excluded body's handle is cached boxed, D-TBD heap sweep). Rapier's `BroadPhase.castRayAndGetNormal` builds two WASM vector
 *   wrappers per call, each registered with a `FinalizationRegistry`, and that grew the heap by about 60 B per
 *   tick for every avoidance pilot (gate-A decision). So this calls the raw broad phase it delegates to, with
 *   two `RawVector`s made once per raycaster and filled in place (the same f32 values Rapier would pass, so the
 *   results are bit-identical). Only a hit still allocates Rapier's raw result, freed at once (D-024).
 */
export function createRaycaster(physics: Physics): RaycastFn {
  const rayOrig: RawVector = RAPIER.VectorOps.intoRaw({ x: 0, y: 0, z: 0 })
  const rayDir: RawVector = RAPIER.VectorOps.intoRaw({ x: 0, y: 0, z: -1 })
  /** Rapier's hit-normal scratch layout (`normal(scratch)` writes x, y, z). */
  const normal = new Float32Array(3)
  /**
   * The excluded body's Rapier handle per entity, cached while that entity keeps the same body. A handle is a float64
   * that is not a small integer; read from `RigidBody.handle` (an unboxed double field) and passed to the binding, V8
   * boxed it into a fresh 16 B number on every cast that excluded a body (five per avoidance pilot per tick). These
   * arrays start with a non-number so V8 keeps tagged elements: a cached handle is boxed once and read back as is.
   */
  const excludeBody: (RAPIER.RigidBody | null)[] = [null]
  const excludeHandle: (number | null)[] = [null]
  return function raycast(ray: Ray, excludeEid: number, out: RayHit): boolean {
    const maxToi = ray.maxToi
    if (!(maxToi > 0)) return false
    const ox = ray.ox
    const oy = ray.oy
    const oz = ray.oz
    const dx = ray.dx
    const dy = ray.dy
    const dz = ray.dz
    rayOrig.x = ox - physics.origin.x
    rayOrig.y = oy - physics.origin.y
    rayOrig.z = oz - physics.origin.z
    rayDir.x = dx
    rayDir.y = dy
    rayDir.z = dz
    const w = physics.world
    const exclude = excludeEid >= 0 ? physics.bodies[excludeEid] : undefined
    let excludeHandleOf: number | undefined
    if (exclude) {
      if (excludeBody[excludeEid] !== exclude) {
        excludeBody[excludeEid] = exclude
        excludeHandle[excludeEid] = exclude.handle
      }
      excludeHandleOf = excludeHandle[excludeEid] ?? undefined
    }
    const broad: RawRayCast = w.broadPhase.raw
    const hit = broad.castRayAndGetNormal(
      w.narrowPhase.raw,
      w.bodies.raw,
      w.colliders.raw,
      rayOrig,
      rayDir,
      maxToi,
      true,
      0,
      undefined,
      undefined,
      excludeHandleOf,
      undefined,
    )
    if (hit === undefined) return false
    const toi = hit.time_of_impact()
    const handle = hit.colliderHandle()
    hit.normal(normal)
    hit.free()
    out.eid = physics.colliderEid.get(handle) ?? EID_ASTEROID
    out.toi = toi
    out.x = ox + dx * toi
    out.y = oy + dy * toi
    out.z = oz + dz * toi
    out.nx = normal[0]
    out.ny = normal[1]
    out.nz = normal[2]
    return true
  }
}
